import { confetti } from '@tsparticles/confetti';
import { Button } from '@velocity/ui';
import React from 'react';
import { useSelector } from 'react-redux';
import { getDocument } from '../../../api/instant-lease-api';
import Layout from '../../../layout';
import { setNotification } from '../../../redux/cache-slice';
import { useAppDispatch } from '../../../redux/store';
import { RootState } from '../../../redux/types';
import { getText } from '../../../utils/getter';
import { formatUTCDateToGerman } from '../../../utils/sanitize-data';
import { ReactComponent as ContractIcon } from '../icons/contract-icon.svg';
import { ReactComponent as DownloadIcon } from '../icons/download-icon.svg';
import Image from '../images/image';
import { ReactComponent as SignedSvg } from '../images/signed-contract.svg';

export interface Document {
  creation_time: string;
  document_type: string;
  file_name: string;
  file_size: number;
  md5hash: string;
  mime_type: string;
  token: string;
}

const StatusCompleted: React.FC = () => {
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  const { accessToken } = useSelector((state: RootState) => state.auth);

  const dispatch = useAppDispatch();

  // Check if document exists
  const isDocumentExist = (documentType: string): Document | undefined =>
    activeApplication?.registered_documents?.find(
      (doc) => doc.document_type === documentType,
    );

  confetti('confetti-left', {
    count: 300,
    position: {
      x: 100,
      y: 80,
    },
    angle: 120,
    spread: 100,
    startVelocity: 100,
    decay: 0.9,
    gravity: 0,
    drift: 0,
    ticks: 10,
    colors: ['#031a6b', '#4ea699', '#5386e4', '#684aef', '#f29559', '#ef476f'],
    shapes: ['circle', 'square', 'polygon'],
    scalar: 1,
    zIndex: 100,
    disableForReducedMotion: true,
  });

  confetti('confetti-right', {
    count: 300,
    position: {
      x: 0,
      y: 80,
    },
    angle: 30,
    spread: 100,
    startVelocity: 100,
    decay: 0.9,
    gravity: 0,
    drift: 0,
    ticks: 10,
    colors: ['#031a6b', '#4ea699', '#5386e4', '#684aef', '#f29559', '#ef476f'],
    shapes: ['circle', 'square', 'polygon'],
    scalar: 1,
    zIndex: 100,
    disableForReducedMotion: true,
  });

  // Download the requested from API
  const downloadFile = (document: Document | undefined) => {
    if (document) {
      const { mime_type, token, file_name } = document;
      getDocument(accessToken, activeApplication?.uuid || '', token, true)
        .then((response) => {
          const blob = window.URL.createObjectURL(
            new Blob([response], { type: mime_type }),
          );
          const link = window.document.createElement('a');
          link.href = blob;
          // link.download = file_name;
          link.setAttribute('download', file_name);
          link.click();
          window.open(blob, '_blank');
        })
        .catch(() => {
          dispatch(
            setNotification({
              notificationVisibility: true,
              notificationHasError: true,
              notificationTitle: 'Das tut uns leid',
              notificationBody: getText('error_unable_to_process_data'),
            }),
          );
        });
    }
  };

  return (
    <Layout
      heading='Herzlichen Glückwunsch!'
      subHeading='Deine Identifikation und digitale Unterschrift wurden erfolgreich abgeschlossen. Dein Antrag wurde erfolgreich übermittelt.'
      hasFullSize={true}
      className='status-overview top-96 grid'
      style={{ gridTemplateRows: '15% 60%' }}
    >
      <div
        className='half-half-columns completed'
        style={{ marginTop: '48px' }}
      >
        <Image
          src='status-overview-completed.jpg'
          fallbackSrc='/theme/kia_flex/images/status-overview-completed.jpg'
          defaultImage={SignedSvg}
        />
        <div>
          <h2 className='content-bold'>Vertragsunterlagen</h2>
          <p className='dark-grey content-normal'>
            Um unsere Umwelt zu schützen, versenden wir den unterzeichneten
            Leasingvertrag <b>nicht mit der Post</b>. Du kannst dir deine
            Vertragsunterlagen hier oder jederzeit in deinem Online-Account
            herunterladen.
          </p>
          <div
            className='contract-download'
            style={{ marginBottom: '48px', marginTop: '16px' }}
          >
            <div
              className='contract small'
              role='presentation'
              onClick={() => {
                downloadFile(isDocumentExist('SIGNED_LEASING_CONTRACT'));
              }}
            >
              <div>
                <ContractIcon />
                <p className='content-normal'>
                  <span className='small-text'>
                    ABGESCHLOSSENER VERTRAG |{' '}
                    {formatUTCDateToGerman(
                      activeApplication?.customer?.document_signed_timestamp,
                    )}
                  </span>
                  <br />
                  <span className='content-bold'>
                    {activeApplication?.offer?.vehicle_data?.full_name}
                  </span>
                </p>
              </div>
              <div>
                <DownloadIcon />
                <p className='content-bold'>Herunterladen</p>
              </div>
            </div>
          </div>
          <div>
            <h2 className='content-bold' style={{ marginBottom: '24px' }}>
              Die letzten Schritte
            </h2>
            <p className='dark-grey content-normal'>
              Im letzten Schritt führen wir eine <b>finale Prüfung</b> der
              Dokumente durch. Diese kann bis zu drei Werktage in Anspruch
              nehmen. Anschließend senden wir <b>dir per E-Mail</b> die
              Auftragsbestätigung und kontaktieren dich bzgl. der Anmeldung und
              Übergabe des Fahrzeugs. Solltest du eine <b>Mietsonderzahlung</b>{' '}
              gewählt haben, bitten wir dich, diese zeitnah zu begleichen. Falls
              du noch Fragen hast, schaue gern in unsere FAQ.
            </p>
          </div>
          <Button
            variant='secondary-filled'
            size='m'
            fullWidth
            margin={{ top: '09' }}
            onClick={() =>
              window.open('https://www.ald-online.de/faq', '_blank')
            }
          >
            Weiter zu den FAQ
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default StatusCompleted;
